import { useFrame } from '@react-three/fiber'
import { useRef } from 'react'
import * as THREE from 'three'
import {
    cameraDampingHorizontal,
    cameraDampingRoll,
    cameraDampingVertical,
} from '../config/constants'
import { store } from '@/src/stores/store'
import { useRunState } from '../provider/useRunState'
import { useTrackController } from '../models/track/useTrackController'
import { CameraAnimator } from './CameraAnimator'

export function CameraController() {
    const trackController = useTrackController()
    const runState = useRunState()
    const cameraPathGroupRef = useRef<THREE.Group | null>(null)
    const targetEulerRef = useRef(new THREE.Euler(0, 0, 0, 'YXZ'))
    const currentEulerRef = useRef(new THREE.Euler(0, 0, 0, 'YXZ'))
    const initializedRef = useRef(false)

    useFrame((_, deltaSeconds) => {
        const cameraPathGroup = cameraPathGroupRef.current
        if (!cameraPathGroup) {
            return
        }

        if (store.debugFreeFlyCameraEnabled) {
            return
        }

        if (runState.phase === 'running' && runState.snapshot.hitFreezeTimer > 0) {
            return
        }

        const trackPosition = trackController.getTrackPlaybackState().trackPosition
        const frame = trackController.frameAtPosition(trackPosition)
        const targetEuler = targetEulerRef.current.setFromQuaternion(
            frame.cameraQuaternion,
            'YXZ',
        )
        const currentEuler = currentEulerRef.current

        cameraPathGroup.position.copy(frame.position)

        if (!initializedRef.current) {
            currentEuler.copy(targetEuler)
            cameraPathGroup.rotation.set(
                currentEuler.x,
                currentEuler.y,
                currentEuler.z,
                'YXZ',
            )
            initializedRef.current = true
            return
        }

        currentEuler.x = dampAngle(
            currentEuler.x,
            targetEuler.x,
            cameraDampingVertical,
            deltaSeconds,
        )
        currentEuler.y = dampAngle(
            currentEuler.y,
            targetEuler.y,
            cameraDampingHorizontal,
            deltaSeconds,
        )
        currentEuler.z = dampAngle(
            currentEuler.z,
            targetEuler.z,
            cameraDampingRoll,
            deltaSeconds,
        )

        cameraPathGroup.rotation.set(
            currentEuler.x,
            currentEuler.y,
            currentEuler.z,
            'YXZ',
        )
    })

    return (
        <group ref={cameraPathGroupRef}>
            <CameraAnimator />
        </group>
    )
}

function dampAngle(current: number, target: number, damping: number, deltaSeconds: number) {
    const delta = Math.atan2(
        Math.sin(target - current),
        Math.cos(target - current),
    )

    return current + delta * (1 - Math.exp(-damping * deltaSeconds))
}
